import { useCallback, useEffect, useRef, useState } from 'react';
import { useServices } from '@/contexts/app-services';
import { useAuth } from '@/contexts/auth';
import type { Block } from '@/entities/chat-message';
import { generateConversationId } from '@/entities/chat-message/operations';
import {
  CHECK_IN_PROMPTS,
  type CheckInResponseCreateInput,
} from '@/entities/check-in-response';
import type { Period } from '@/entities/types';
import { logError } from '@/services/logger';
import { toDateTimeStringWithTimezone } from '@/utils/datetime';

const IDLE_DEBOUNCE_MS = 1000;
const IMMEDIATE_CHAR_COUNT = 150;
const DEBOUNCE_CHAR_COUNT = 15;
const FALLBACK_INTERVAL_MS = 60_000;
const MIN_COOLDOWN_MS = 5000;

interface UseCheckInInsightsArgs {
  period: Period;
  targetStartAt: Date;
  responses: CheckInResponseCreateInput;
  isPaused: boolean;
}

interface UseCheckInInsightsReturn {
  insights: Block[];
  isExtracting: boolean;
  resetInsights: () => void;
}

function getTotalChars(responses: CheckInResponseCreateInput): number {
  return Object.values(responses).reduce((sum, val) => sum + val.length, 0);
}

function hasContent(responses: CheckInResponseCreateInput): boolean {
  return getTotalChars(responses) > 0;
}

function isWithinCooldown(lastAttemptTime: number): boolean {
  return lastAttemptTime > 0 && Date.now() - lastAttemptTime < MIN_COOLDOWN_MS;
}

function canExtract(
  responses: CheckInResponseCreateInput,
  isPaused: boolean,
  lastAttemptTime: number,
): boolean {
  return (
    hasContent(responses) && !isPaused && !isWithinCooldown(lastAttemptTime)
  );
}

function sumAbsDeltas(snapshots: number[]): number {
  return snapshots.reduce((sum, val, i) => {
    if (i === 0) {
      return sum;
    }
    const prev = snapshots[i - 1];
    if (prev === undefined) {
      return sum;
    }
    return sum + Math.abs(val - prev);
  }, 0);
}

export function useCheckInInsights({
  period,
  targetStartAt,
  responses,
  isPaused,
}: UseCheckInInsightsArgs): UseCheckInInsightsReturn {
  const { user } = useAuth();
  const { ai } = useServices();
  const conversationId = user
    ? generateConversationId(user.id, period, targetStartAt)
    : '';
  const [insights, setInsights] = useState<Block[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);

  const extractionVersionRef = useRef(0);
  const charSnapshotsRef = useRef([0]);
  const lastAttemptTimeRef = useRef(0);
  const insightsRef = useRef<Block[]>([]);
  const hasResponseContent = hasContent(responses);

  useEffect(
    function syncInsightsRef() {
      insightsRef.current = insights;
    },
    [insights],
  );

  const resetInsights = useCallback(() => {
    extractionVersionRef.current += 1;
    charSnapshotsRef.current = [0];
    lastAttemptTimeRef.current = 0;
    insightsRef.current = [];
    setInsights([]);
    setIsExtracting(false);
  }, []);

  const triggerExtraction = useCallback(async () => {
    if (!canExtract(responses, isPaused, lastAttemptTimeRef.current)) {
      return;
    }

    const now = Date.now();
    const version = ++extractionVersionRef.current;
    charSnapshotsRef.current = [getTotalChars(responses)];
    lastAttemptTimeRef.current = now;

    setIsExtracting(true);

    const journalEntries = CHECK_IN_PROMPTS.map((prompt) => ({
      promptKey: prompt.key,
      promptText: prompt.text,
      response: responses[prompt.key] ?? '',
    }));

    try {
      const result = await ai.extractCheckInInsights({
        sessionId: conversationId,
        journalEntries,
        currentInsights: insightsRef.current,
        context: {
          user: { currentDateTime: toDateTimeStringWithTimezone() },
        },
      });

      if (version !== extractionVersionRef.current) {
        return;
      }

      const withKeys = result.map((block) => {
        if (!block.key) {
          return { ...block, key: crypto.randomUUID() };
        }
        return block;
      });

      setInsights(withKeys);
    } catch (err: unknown) {
      logError(err, 'Failed to extract check-in insights');
    } finally {
      if (version === extractionVersionRef.current) {
        setIsExtracting(false);
      }
    }
  }, [ai, conversationId, responses, isPaused]);

  useEffect(
    function extractOnTyping() {
      const currentTotal = getTotalChars(responses);
      const lastSnapshot = charSnapshotsRef.current.at(-1) ?? 0;
      if (currentTotal !== lastSnapshot) {
        charSnapshotsRef.current.push(currentTotal);
      }

      const editDistance = sumAbsDeltas(charSnapshotsRef.current);

      if (editDistance < DEBOUNCE_CHAR_COUNT) {
        return;
      }

      if (isPaused || !hasContent(responses)) {
        return;
      }

      if (isWithinCooldown(lastAttemptTimeRef.current)) {
        const remaining =
          MIN_COOLDOWN_MS - (Date.now() - lastAttemptTimeRef.current);
        const timer = setTimeout(() => {
          void triggerExtraction();
        }, remaining);
        return () => {
          clearTimeout(timer);
        };
      }

      if (editDistance >= IMMEDIATE_CHAR_COUNT) {
        void triggerExtraction();
        return undefined;
      }

      const timer = setTimeout(() => {
        void triggerExtraction();
      }, IDLE_DEBOUNCE_MS);
      return () => {
        clearTimeout(timer);
      };
    },
    [responses, isPaused, triggerExtraction],
  );

  useEffect(
    function extractOnFallbackInterval() {
      const interval = setInterval(() => {
        if (
          !isPaused &&
          charSnapshotsRef.current.length > 1 &&
          Date.now() - lastAttemptTimeRef.current >= FALLBACK_INTERVAL_MS
        ) {
          void triggerExtraction();
        }
      }, FALLBACK_INTERVAL_MS);

      return () => {
        clearInterval(interval);
      };
    },
    [isPaused, triggerExtraction],
  );

  return {
    insights: hasResponseContent ? insights : [],
    isExtracting,
    resetInsights,
  };
}
